import type { HelpArticle } from '../../../components/help/types';

export const workflowApprovalsArticles: HelpArticle[] = [
  {
    id: 'workflows-overview',
    title: 'Understanding Workflows',
    summary: 'Learn how document approval workflows work.',
    category: 'workflows',
    order: 1,
    keywords: ['workflow', 'approval', 'routing', 'process', 'chain'],
    content: `
# Understanding Workflows

Workflows route documents through approval chains for review and authorization.

## What is a Workflow?

A workflow is a defined process that:
- Routes documents to specific approvers
- Tracks approval status at each stage
- Enforces organizational hierarchy
- Maintains an audit trail

## Workflow Stages

1. **Initiation**: Document is submitted for approval
2. **Review Stages**: Document passes through approvers
3. **Decision**: Each approver approves, rejects, or requests changes
4. **Completion**: Final approval or rejection

## Workflow Types

### Sequential Workflow
- Document moves through approvers one at a time
- Each must approve before moving to the next
- Order is strictly followed

### Parallel Workflow
- Multiple approvers review simultaneously
- All or majority must approve
- Faster for time-sensitive documents

## Your Role

You may be:
- **Initiator**: Submit documents for approval
- **Approver**: Review and make decisions
- **Observer**: View progress without action
    `,
  },
  {
    id: 'workflows-submitting',
    title: 'Submitting for Approval',
    summary: 'How to submit documents for workflow approval.',
    category: 'workflows',
    order: 2,
    keywords: ['submit', 'request', 'approval', 'initiate', 'start'],
    content: `
# Submitting for Approval

Send documents through the approval workflow.

## Starting a Workflow

1. Open the document to submit
2. Click **Submit for Approval**
3. Select the workflow template
4. Add notes for approvers (optional)
5. Click **Submit**

## Choosing a Workflow

Select based on:
- Document type
- Required approvers
- Urgency level
- Organizational requirements

## Tracking Your Submissions

1. Go to **Workflows** in the sidebar
2. Click **My Requests**
3. View all submitted documents
4. See current status and stage

## Submission Statuses

- **Pending**: Waiting for current approver
- **Approved**: All approvals received
- **Rejected**: An approver denied
- **Returned**: Changes requested

## Withdrawing a Submission

If needed, you can withdraw:
1. Open the submission
2. Click **Withdraw**
3. Provide a reason
4. Confirm withdrawal

> Note: You cannot withdraw if already approved at any stage.
    `,
  },
  {
    id: 'workflows-reviewing',
    title: 'Reviewing and Approving',
    summary: 'How to review documents and make approval decisions.',
    category: 'workflows',
    order: 3,
    keywords: ['review', 'approve', 'reject', 'decision', 'action'],
    content: `
# Reviewing and Approving

When documents are routed to you for approval, here's how to handle them.

## Finding Pending Approvals

1. Check your **Dashboard** for pending tasks
2. Or go to **Workflows** > **Pending Approvals**
3. Click any item to review

## Reviewing Documents

1. Read the document carefully
2. Review attached files
3. Check initiator's notes
4. See previous approver comments

## Making a Decision

### Approve
- Click **Approve**
- Add optional comments
- Document moves to next stage

### Reject
- Click **Reject**
- Provide rejection reason (required)
- Workflow ends, initiator notified

### Request Changes
- Click **Request Changes**
- Describe needed changes
- Document returns to initiator

## Best Practices

- Review promptly to avoid delays
- Add helpful comments for context
- Reject with clear, actionable feedback
- Consider the full approval chain
    `,
  },
];
